import { type FormEvent, useRef, useState } from "react";
import { Link } from "react-router";
import { PASSWORD_MIN_LENGTH } from "../../shared/constants";
import { registerSchema } from "../../shared/schemas";
import { api } from "../api/client";
import { useAuthOptions } from "../auth/useAuthOptions";
import { GoogleButton } from "../components/GoogleButton";
import { Alert, AuthLayout, focusFirstInvalid, TextField } from "../components/ui";
import { apiFailure, type FieldErrors, validate } from "./forms";

export function RegisterPage() {
  const formRef = useRef<HTMLFormElement>(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", acceptTerms: false });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const options = useAuthOptions();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setMessage("");
    const v = validate(registerSchema, form);
    if (!v.ok) {
      setErrors(v.errors);
      focusFirstInvalid(formRef.current);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      await api("POST", "/auth/register", v.data);
      setSentTo(v.data.email);
    } catch (err) {
      const f = apiFailure(err);
      setErrors(f.fields);
      setMessage(f.message);
      focusFirstInvalid(formRef.current);
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    if (!sentTo) return;
    try {
      await api("POST", "/auth/resend-verification", { email: sentTo });
      setResent(true);
    } catch (err) {
      setMessage(apiFailure(err).message);
    }
  }

  if (options && !options.registrationOpen) {
    return (
      <AuthLayout>
        <div className="stack" style={{ gap: 8 }}>
          <h1 className="h1">Înscrieri închise momentan</h1>
          <p className="muted" style={{ margin: 0 }}>
            Conturile noi se creează momentan doar prin invitație.{" "}
            {options.contactUrl ? <a href={options.contactUrl}>Scrie-ne</a> : "Scrie-ne"} și îți trimitem una.
          </p>
        </div>
        <div className="auth-foot">
          Ai primit deja o invitație? <Link to="/login">Intră în cont</Link>
        </div>
      </AuthLayout>
    );
  }

  if (sentTo) {
    return (
      <AuthLayout>
        <div className="stack" style={{ gap: 8 }}>
          <h1 className="h1">Verifică e-mailul</h1>
          <p className="muted" style={{ margin: 0 }}>
            Ți-am trimis un link de confirmare la{" "}
            <strong style={{ color: "var(--color-text)" }}>{sentTo}</strong>. Deschide-l ca să îți activezi
            contul. Linkul este valabil 24 de ore.
          </p>
        </div>
        {message && (
          <Alert tone="error" live>
            {message}
          </Alert>
        )}
        {resent ? (
          <Alert tone="info" live>
            Am trimis un link nou. Verifică și dosarul Spam.
          </Alert>
        ) : (
          <button type="button" className="btn btn-secondary" onClick={resend}>
            Trimite din nou linkul
          </button>
        )}
        <p className="auth-foot">
          <Link to="/login">Înapoi la autentificare</Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="stack" style={{ gap: 8 }}>
        <h1 className="h1">Creează contul</h1>
        <p className="muted" style={{ margin: 0 }}>
          Încarcă meniul existent, verifică-l și publică-l. Nimic nu apare public fără acordul tău.
        </p>
      </div>
      {message && (
        <Alert tone="error" live>
          {message}
        </Alert>
      )}
      {options?.googleEnabled && <GoogleButton withTerms />}
      {options && !options.emailEnabled ? (
        <Alert tone="info">
          {options.googleEnabled
            ? "Înregistrarea cu e-mail și parolă nu este disponibilă momentan; folosește contul Google."
            : "Înregistrarea nu este disponibilă momentan. Încearcă din nou mai târziu."}
        </Alert>
      ) : (
        <form ref={formRef} onSubmit={onSubmit} noValidate>
          <TextField
            id="reg-name"
            label="Numele tău"
            autoComplete="name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            error={errors.name}
          />
          <TextField
            id="reg-email"
            label="E-mail"
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            error={errors.email}
          />
          <TextField
            id="reg-password"
            label="Parolă"
            type="password"
            autoComplete="new-password"
            help={`Minimum ${PASSWORD_MIN_LENGTH} caractere.`}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            error={errors.password}
          />
          <div className="field">
            <label className="check" htmlFor="reg-terms">
              <input
                id="reg-terms"
                type="checkbox"
                checked={form.acceptTerms}
                aria-invalid={errors.acceptTerms ? "true" : "false"}
                aria-labelledby="reg-terms-text"
                aria-describedby={errors.acceptTerms ? "reg-terms-error" : undefined}
                onChange={(e) => setForm({ ...form, acceptTerms: e.target.checked })}
              />
              <span id="reg-terms-text">
                Accept <Link to="/termeni">Termenii de utilizare</Link> și am citit{" "}
                <Link to="/confidentialitate">Nota de confidențialitate</Link>.
              </span>
            </label>
            {errors.acceptTerms && (
              <div id="reg-terms-error" className="field-error">
                {errors.acceptTerms}
              </div>
            )}
          </div>
          <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
            {busy ? "Se creează contul…" : "Creează contul"}
          </button>
        </form>
      )}
      <p className="auth-foot">
        Ai deja cont? <Link to="/login">Intră în cont</Link>
      </p>
    </AuthLayout>
  );
}
